import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { CheckCircle2, Download, FolderOpen, Loader2, XCircle } from "lucide-react";
import { Button } from "../components/ui/button";
import { getReport, getReportCheckoutStatus, reportFileUrl } from "../lib/api";

const ReportBrand = () => (
  <header data-testid="report-brand-header" className="bg-white border-b-2 border-sky-700 py-3 text-center">
    <img src="/brand/phdg-logo.png" alt="GreenFlow Plant Health & Diagnostic Group" className="mx-auto h-auto w-40 sm:w-48" />
    <div className="text-[11px] text-slate-600">A Division of GreenFlow Landcare &amp; Irrigation, LLC</div>
  </header>
);

export function ReportSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const id = params.get("session_id");
  const [state, setState] = useState("checking");
  useEffect(() => {
    let tries = 0;
    let alive = true;
    const poll = async () => {
      try {
        const s = await getReportCheckoutStatus(id);
        if (!alive) return;
        if (s.report_url) return navigate(s.report_url, { replace: true });
        if (s.status === "expired") return setState("expired");
      } catch { /* retry */ }
      tries += 1;
      if (tries < 30) setTimeout(poll, 2000);
      else setState("timeout");
    };
    if (id) poll(); else setState("timeout");
    return () => { alive = false; };
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className="min-h-[100dvh] bg-[#f8faf8]">
      <ReportBrand />
      <main data-testid="report-success-page" className="mx-auto max-w-md px-5 py-12 text-center">
        {state === "checking" ? <><Loader2 className="mx-auto animate-spin text-emerald-600" size={40} />
          <h1 className="mt-4 font-display text-2xl font-bold text-[#092C15]">Confirming your payment with Stripe…</h1>
          <p className="mt-2 text-sm text-slate-600">Your report is released only after Stripe confirms payment. You won't be charged twice.</p></>
          : <><XCircle className="mx-auto text-amber-500" size={44} />
          <h1 data-testid="report-unconfirmed" className="mt-4 font-display text-2xl font-bold text-[#092C15]">We couldn't confirm this payment yet</h1>
          <p className="mt-2 text-sm text-slate-600">Nothing is released until payment is verified. If you were charged, your report will be emailed as soon as it's confirmed.</p></>}
      </main>
    </div>
  );
}

export default function ReportView() {
  const { reportId } = useParams();
  const [params] = useSearchParams();
  const t = params.get("t");
  const [r, setR] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    let alive = true;
    let timer;
    const load = async () => {
      try {
        const d = await getReport(reportId, t);
        if (!alive) return;
        setR(d);
        if (["paid", "generating"].includes(d.status)) timer = setTimeout(load, 4000);
      } catch { setErr(true); }
    };
    load();
    return () => { alive = false; clearTimeout(timer); };
  }, [reportId, t]);

  return (
    <div className="min-h-[100dvh] bg-[#f8faf8]">
      <ReportBrand />
      <main data-testid="report-view-page" className="mx-auto max-w-4xl px-4 sm:px-6 py-8">
        {err && <p data-testid="report-not-found" className="text-center text-slate-600">This report link isn't valid.</p>}
        {r && (
          <>
            <div className="rounded-3xl bg-white border border-emerald-100 p-5 sm:p-6">
              <div className="flex items-center gap-2 text-emerald-700 text-xs font-bold uppercase tracking-widest"><CheckCircle2 size={16} /> {r.admin_comp ? "Complimentary preview" : "Payment confirmed"} · Case {r.case_id}</div>
              <h1 data-testid="report-title" className="mt-1 font-display text-2xl sm:text-3xl font-bold text-[#092C15]">{r.title || "Your Personalized Diagnostic Brief"}</h1>
              {r.package === "printed" && <p className="mt-1 text-sm text-amber-700">Printed package pilot: your digital brief is ready now. Printed mailing is not active yet.</p>}
              {r.status === "ready" ? (
                <div className="mt-4 flex flex-col sm:flex-row gap-3">
                  <Button data-testid="report-download-button" asChild className="rounded-xl h-12 bg-[#14532D] hover:bg-[#0F3F22] font-bold">
                    <a href={reportFileUrl(reportId, t, "pdf", true)}><Download size={16} className="mr-2" />DOWNLOAD MY REPORT (PDF)</a></Button>
                  {r.case_url && <Button data-testid="report-case-link" asChild variant="outline" className="rounded-xl h-12 font-bold">
                    <a href={r.case_url}><FolderOpen size={16} className="mr-2" />RETURN TO MY PRIVATE CASE</a></Button>}
                </div>
              ) : r.status === "error" ? (
                <p data-testid="report-error" className="mt-3 text-sm text-amber-700">We hit a snag preparing your report. GreenFlow has it and will finish it for you. You won't be charged again.</p>
              ) : (
                <p data-testid="report-generating" className="mt-3 text-sm text-slate-600 flex items-center gap-2"><Loader2 size={16} className="animate-spin" /> Preparing your report from your case. This usually takes under a minute…</p>
              )}
            </div>
            {r.status === "ready" && (
              <iframe data-testid="report-preview-frame" title="Report preview" src={reportFileUrl(reportId, t, "html")}
                className="mt-5 w-full h-[75vh] rounded-2xl border border-slate-200 bg-white" sandbox="" />
            )}
          </>
        )}
      </main>
    </div>
  );
}
